// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable/Clickable";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import removeProduct from "@werner/App/Handlers/MiniQuickOrder/RemoveProduct";
import removeQuickOrderLine from "@werner/App/Handlers/MiniQuickOrder/RemoveQuickOrderLine";
import searchProducts from "@werner/App/Handlers/MiniQuickOrder/SearchProducts";
import setProduct from "@werner/App/Handlers/MiniQuickOrder/SetProduct";
import { CustomAppState } from "@werner/App/Store/Reducers";
import Close from "@werner/XDL/Icons/Close";
import Trash from "@werner/XDL/Icons/Trash";
import { color } from "@werner/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quickOrderLineId: string;
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
    index: number;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const quickOrderLine = state.xQuickOrderLine.quickOrderLines[ownProps.quickOrderLineId];
    const product = getProductState(state, quickOrderLine?.selectedProductInfo?.productId).value;
    return {
        isSearching: quickOrderLine?.isSearching,
        searchResults: quickOrderLine?.searchResults,
        selectedProductInfo: quickOrderLine?.selectedProductInfo,
        product,
        errorType: quickOrderLine?.errorType,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    removeProduct,
    removeQuickOrderLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: InjectableCss;
    qtyTextField?: TextFieldPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    removeClickable?: ClickablePresentationProps;
    removeIcon?: IconPresentationProps;
    dropdownWrapper?: InjectableCss;
    clearIcon?: IconProps;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            width: 100%;
        `,
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    removeClickable: {
        css: css`
            &:hover > ${IconWrapper} {
                color: ${color("text.main")};
            }
        `,
    },
    removeIcon: {
        src: Trash,
        size: 20,
        color: "common.ui3",
    },
    searchDynamicDropdown: {
        iconProps: {
            src: undefined,
        },
    },
    qtyTextField: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                max-width: 110px;
            `,
            inputSelect: css`
                padding-left: 10px !important;
                padding-right: 10px !important;
                text-align: center;
            `,
        },
    },
    dropdownWrapper: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    clearIcon: {
        size: 14,
        css: css`
            position: absolute;
            right: 15px;
            top: 34px;
            cursor: pointer;
            &:hover {
                color: ${color("primary")};
            }
        `,
    },
};

const ENTER_KEY = 13;

const QuickOrderProductSelector = ({
    quickOrderLineId,
    onSelectProduct,
    skipInventoryValidation,
    denyFixedConfiguration,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    removeProduct,
    removeQuickOrderLine,
    index,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [selectedProductId, setSelectedProductId] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    const [shouldAddOrder, setShouldAddOrder] = useState(false);

    useEffect(() => {
        if (!shouldAddOrder) {
            return;
        }
        if (selectedProductInfo && !errorMessage && product && Number(qty) >= 1) {
            setShouldAddOrder(false);
            selectProduct();
        }
    }, [shouldAddOrder, selectedProductInfo, errorMessage, product]);

    useEffect(() => {
        if (!selectedProductInfo) {
            return;
        }

        selectProduct();
    }, [qty]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper {...styles.optionWrapper} data-test-selector={`productSelector_search_${product.id}`}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage
                            {...styles.productImage}
                            src={product.image}
                            altText={`${product.displayName} Image`}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText} data-test-selector="productSelector_ProductTitle">
                            {product.displayTitle || product.title}
                        </Typography>
                        <Typography {...styles.autocompleteErpText}>
                            {product.displayErpNumber || product.erpNumber}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    lineId: quickOrderLineId,
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                    onSuccess: _ => {
                        setShouldAddOrder(true);
                    },
                });
            } else {
                setProduct({
                    lineId: quickOrderLineId,
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                    onSuccess: _ => {
                        setShouldAddOrder(true);
                    },
                });
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ lineId: quickOrderLineId, query });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");

        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            removeProduct({ productId: selectedProductInfo.productId, index });
        }
        if (event.target.value && event.target.value.length < 3) {
            return;
        }
        debouncedSearchProducts(event.target.value);
    };

    const selectProduct = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = Math.max(Number(qty) || 1, product.minimumOrderQty || 1);
        onSelectProduct(newProductInfo, product);
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({ lineId: quickOrderLineId, searchTerm, skipInventoryValidation, denyFixedConfiguration });
            }
        }
    };

    const removeProductHandler = () => {
        clearButtonHandler();
        removeQuickOrderLine({ lineId: quickOrderLineId });
    };

    const clearButtonHandler = () => {
        if (selectedProductInfo) {
            removeProduct({ productId: selectedProductInfo.productId, index });
            setSelectedProductId("");
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="productSelector">
            <StyledWrapper {...styles.dropdownWrapper}>
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    label={translate("Keyword or Part #")}
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={onKeyPress}
                    filterOption={() => true}
                    selected={selectedProductId}
                    isLoading={isSearching}
                    options={options}
                    error={errorMessage}
                    data-test-selector="productSelector_search"
                />
                {selectedProductId && <Icon {...styles.clearIcon} src={Close} onClick={clearButtonHandler} />}
            </StyledWrapper>
            <TextField
                label={translate("QTY")}
                value={qty}
                onChange={e => {
                    setQty(e.currentTarget.value);
                }}
                {...styles.qtyTextField}
                type="qty"
                data-test-selector="productSelector_qty"
            />
            <Clickable {...styles?.removeClickable} onClick={removeProductHandler} aria-label="remove-quick-order-line">
                <Icon {...styles?.removeIcon} />
            </Clickable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductSelector);
